import { useId, useLayoutEffect, useRef, useState } from "react";
import { useHudStore } from "../store/hudStore";
import { DOMAINS } from "../data/seed";
import { Badge, Button, Field, Select } from "../design-system/components";
import "./QuestForm.css";

export default function AddQuestForm() {
  const st = useHudStore();
  const mainHelperId = `quest-main-help-${useId()}`;
  const [attempted, setAttempted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const returnFocusRef = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  useLayoutEffect(() => {
    formRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    return () => { if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus(); };
  }, []);

  return (
    <form ref={formRef} className="quest-form quest-form--add" aria-label="Add a quest" noValidate onSubmit={(event) => {
      event.preventDefault();
      setAttempted(true);
      if (st.draftTitle.trim()) st.saveQuest();
      else formRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    }}>
      <Field label="Quest title" required value={st.draftTitle} onChange={(event) => st.setDraftTitle(event.target.value)} placeholder="What's the quest?" error={attempted && !st.draftTitle.trim() ? "Give this quest a title." : undefined} />

      <fieldset className="quest-form-group" aria-describedby={mainHelperId}>
        <legend className="quest-form-label">Main quests</legend>
        <div className="quest-form-picks">
          {st.mains.map((main) => <Button key={main.id} size="compact" variant={st.draftMains.includes(main.id) ? "primary" : "secondary"} aria-pressed={st.draftMains.includes(main.id)} onClick={() => st.draftPickMain(main.id)}>{main.name}</Button>)}
        </div>
        <p id={mainHelperId} className="quest-form-help">Pick the goals this serves. The first pick is the primary.</p>
      </fieldset>

      <Select label="Domain" value={st.draftDomain} onChange={(event) => st.draftPickDomain(event.target.value)} helper="Choose the life area this quest supports.">
        {DOMAINS.map((domain) => <option key={domain.name} value={domain.name}>{domain.name}</option>)}
      </Select>

      <div className="quest-form-group">
        <p className="quest-form-label">Criteria: how you'll know it's done</p>
        {st.draftSteps.length > 0 && <ul className="quest-form-steps">
          {st.draftSteps.map((text, index) => <li key={index} className="quest-form-step">
            <i className="fa-regular fa-square" aria-hidden="true" />
            <span>{text}</span>
            <Button variant="quiet" size="compact" icon="fa-solid fa-xmark" onClick={() => st.removeStep(index)} aria-label={`Remove step ${index + 1}: ${text}`} />
          </li>)}
        </ul>}
        <div className="quest-form-add-step">
          <Field label="Add a step" value={st.draftStep} onChange={(event) => st.setDraftStep(event.target.value)} onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            st.addStep();
          }} placeholder="For example, review every wrong answer" />
          <Button onClick={st.addStep} disabled={!st.draftStep.trim()}>Add</Button>
        </div>
      </div>

      <div className="quest-form-footer">
        <div className="quest-form-reward">
          <span className="quest-form-label">Reward</span>
          <div className="quest-form-stepper" role="group" aria-label="Quest XP reward">
            <Button size="compact" icon="fa-solid fa-minus" onClick={st.xpDown} disabled={st.draftXp <= 10} aria-label="Decrease reward by 10 XP" />
            <Badge tone="warning">{st.draftXp} XP</Badge>
            <Button size="compact" icon="fa-solid fa-plus" onClick={st.xpUp} disabled={st.draftXp >= 300} aria-label="Increase reward by 10 XP" />
          </div>
        </div>
        <div className="quest-form-actions">
          <Button onClick={st.cancelAdd}>Cancel</Button>
          <Button type="submit" variant="primary">Add quest</Button>
        </div>
      </div>
    </form>
  );
}
